import type React from "react";
import { cx } from "@/utils/cx";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";

type PageLayoutProps = {
  title: string;
  subtitle: string;
  icon: React.ComponentType<React.CSSProperties & React.SVGProps<SVGSVGElement>>;
  callToAction?: React.ReactNode;
  backgroundColor?: string;
  children?: React.ReactNode;
  /** Caps the page at one viewport tall with no page-level scroll — for pages whose
   * content manages its own internal scroll (e.g. a sticky, viewport-height-bound list). */
  fitViewport?: boolean;
  /** Pushes the header and content left to make room for a side drawer (e.g. a filter panel)
   * instead of letting the drawer cover them. */
  drawerOpen?: boolean;
  contentGap?: string;
};

export const PageLayout = ({
  title,
  subtitle,
  icon: Icon,
  callToAction,
  backgroundColor,
  children,
  fitViewport,
  drawerOpen,
  contentGap = "gap-5",
}: PageLayoutProps) => {
  return (
    <div
      className={cx("flex flex-col w-full gap-5 items-center", fitViewport ? "overflow-hidden" : "min-h-screen")}
      style={{
        background: backgroundColor ?? "var(--color-unbox-cream-25)",
        ...(fitViewport ? { height: "calc(100vh - var(--sidebar-top-offset, 0px))" } : undefined),
      }}
    >
      <div
        className={cx(
          `${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col gap-5 px-16 pt-16 pb-12`,
          fitViewport && "flex-1 min-h-0",
        )}
      >
        <div className="flex flex-row h-full w-full">
          <div
            className={cx(
              "flex flex-col h-full min-w-0 w-full transition-all duration-300",
              contentGap,
              drawerOpen && "mr-96",
            )}
          >
            <div className="flex flex-row items-start justify-between flex-shrink-0 mb-3">
              <div className="flex flex-col">
                <h1
                  data-testid="page-title"
                  className="text-xl font-normal text-primary flex flex-row items-center gap-2 mb-1"
                >
                  <Icon className="w-5 h-5" strokeWidth={1.8} />
                  {title}
                </h1>
                <span className="text-quaternary text-sm">{subtitle}</span>
              </div>
              {callToAction}
            </div>
            {/* children render as flat flex-col siblings of the header (with the same
                gap-5). Pass multiple top-level blocks as JSX children directly — or a
                <>fragment</> when returning them from an expression — never wrap them in
                an extra div, or that div's own height/overflow will fight this column's
                and break scrolling. A child meant to fill remaining space (rather than
                size to its own content) needs flex-1 min-h-0 itself. */}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
